import React, { useRef, useState } from 'react';
import { getImageUrl, handleImageError } from '../utils/api';

// Swipeable product image slider. Touch and trackpad swipes scroll the
// scroll-snap track natively; mouse drags are handled with pointer events.
// Remount with a `key` per product to reset to the first image.
const ProductGallery = ({ images, alt }) => {
  const trackRef = useRef(null);
  const drag = useRef(null);
  const snapTimer = useRef(null);
  const [active, setActive] = useState(0);
  const multiple = images.length > 1;

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(images.length - 1, i));
    el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  };

  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || !multiple) return;
    const el = trackRef.current;
    clearTimeout(snapTimer.current);
    drag.current = { x: e.clientX, left: el.scrollLeft, start: Math.round(el.scrollLeft / el.clientWidth) };
    el.classList.add('dragging');
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    trackRef.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
  };

  const endDrag = (e) => {
    if (!drag.current) return;
    const el = trackRef.current;
    const dx = e.clientX - drag.current.x;
    const step = Math.abs(dx) > el.clientWidth * 0.12 ? (dx < 0 ? 1 : -1) : 0;
    goTo(drag.current.start + step);
    drag.current = null;
    // Re-enable snapping once the smooth scroll has settled.
    snapTimer.current = setTimeout(() => el.classList.remove('dragging'), 400);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') goTo(active + 1);
    if (e.key === 'ArrowLeft') goTo(active - 1);
  };

  return (
    <div className="pd-gallery">
      <div className="pd-gallery-main">
        <div
          ref={trackRef}
          className={`pd-gallery-track${multiple ? ' swipeable' : ''}`}
          onScroll={handleScroll}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={multiple ? onKeyDown : undefined}
          tabIndex={multiple ? 0 : undefined}
          aria-label={multiple ? 'Product images — swipe to browse' : undefined}
        >
          {images.map((img, i) => (
            <div key={i} className="pd-gallery-slide">
              <img src={getImageUrl(img)} onError={handleImageError} alt={multiple ? `${alt} (${i + 1} of ${images.length})` : alt} draggable={false} />
            </div>
          ))}
        </div>
        {multiple && (
          <div className="pd-gallery-dots">
            {images.map((_, i) => (
              <button key={i} type="button" className={i === active ? 'active' : ''} onClick={() => goTo(i)} aria-label={`Show image ${i + 1}`} />
            ))}
          </div>
        )}
      </div>

      {multiple && (
        <div className="pd-gallery-thumbs">
          {images.map((img, i) => (
            <button key={i} type="button" className={i === active ? 'active' : ''} onClick={() => goTo(i)} aria-label={`Show image ${i + 1}`}>
              <img src={getImageUrl(img)} onError={handleImageError} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
